import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
} from "@@/shared/date-time/iso-and-unix";

import type { Discount} from "@@/shared/discounts/discount";
import { discountZodSchema } from "@@/shared/discounts/discount";
import type { EntityState} from "@@/shared/entity-status";
import { entityStateZodSchema } from "@@/shared/entity-status";
import type { Order } from "@@/shared/orders/order";
import type { OrderId } from "@@/shared/orders/order-id";
import type { Price} from "@@/shared/prices/price";
import { priceZodSchema, zeroPrice } from "@@/shared/prices/price";
import type { UserId} from "@@/shared/users/user-id";
import { userIdZodSchema } from "@@/shared/users/user-id";
import { exists } from "@@/utilities/exists";
import { z } from "zod";
import { orderIdZodSchema } from "../orders/order-id";
import { sumPrices } from "../prices/sum-prices";
import type { CustomerId} from "../users/customer-id";
import { customerIdZodSchema } from "../users/customer-id";
import type { OrderGroupId} from "./order-group-id";
import { orderGroupIdZodSchema } from "./order-group-id";

type OrderGroup = {
    readonly _id: OrderGroupId;
    readonly _type: "ORDER_GROUP";
    readonly _status: EntityState;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly slug: string;
    readonly reference: string;
    readonly orderIds: OrderId[];
    readonly discounts: Discount[];
    readonly customerId: UserId | CustomerId;
    readonly checkoutId: string;
    readonly customerServiceFee: Price;
};

const orderGroupZodSchema: z.ZodSchema<OrderGroup> = z.object({
    _id: orderGroupIdZodSchema,
    _type: z.literal("ORDER_GROUP"),
    _status: entityStateZodSchema,
    _createdAt: isoAndUnixZodSchema,
    slug: z.string(),
    reference: z.string(),
    orderIds: z.array(orderIdZodSchema),
    discounts: z.array(discountZodSchema),
    customerId: userIdZodSchema.or(customerIdZodSchema),
    checkoutId: z.string(),
    customerServiceFee: priceZodSchema,
});

/**
 * Calculate price of order group\
 * _Note: without discounts_
 * @param {{orderGroup: OrderGroup, orders: Order[]}} params
 * @returns {Price}
 */
function calculateOrderGroupPrice(params: {
    orderGroup: OrderGroup;
    orders: Order[];
}): Price {
    // Destructure params
    const { orderGroup } = params;

    // Make sure only orders within the group are part of the orders array
    const orders = orderGroup.orderIds
        .map(orderId => params.orders.find(order => order._id === orderId))
        .filter(exists);

    // Make sure there are orders at all in the order group
    if (!orderGroup.orderIds.length && !orders.length)
        throw new Error("no orders in order group");

    // Make sure all order ids in the order group are
    // present in the orders array
    if (
        !orderGroup.orderIds.every(orderId =>
            orders.some(order => order._id === orderId),
        )
    )
        throw new Error("missing order(s) defined in order group");

    // Initiate an array of all prices to sum
    const prices: Price[] = [];

    // Check for legacy customer service fees
    // and add it if present
    if (orderGroup.customerServiceFee)
        prices.push(orderGroup.customerServiceFee);

    // Add all order prices and order
    // shipping costs to the array
    orders.forEach(order => {
        const shippingCost =
            order.delivery.deliveryMethodType === "HOME_DELIVERY"
                ? order.delivery.price
                : zeroPrice;

        prices.push(shippingCost);
        prices.push(order.totalPrice);
    });

    // Sum up the result and return it
    return sumPrices(prices);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isOrderGroup = (input: any): input is OrderGroup => {
    return input?._type === "ORDER_GROUP";
};

export { calculateOrderGroupPrice, isOrderGroup, orderGroupZodSchema };
export type { OrderGroup };
